import {
  createContext,
  FC,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useContext,
  useState,
} from 'react';

import MuiDialog, { DialogProps as MuiDialogProps } from '@mui/material/Dialog';

type Props = Omit<MuiDialogProps, 'open'> & {
  modal?: boolean;
  activateButton: (open: (event: SyntheticEvent) => void) => ReactNode;
};

export type DialogProps = Props;

type DialogContextType = {
  close: () => void;
};

const DialogContext = createContext<DialogContextType>({
  close: () => {},
});

export const useDialog = () => useContext(DialogContext);

const Dialog: FC<Props> = props => {
  const { children, modal = true, activateButton, ...rest } = props;
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const open = useCallback((event: SyntheticEvent) => {
    event.stopPropagation();

    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleDialogClose = useCallback(
    (event: SyntheticEvent, reason: any) => {
      event.stopPropagation();

      if (!modal || reason === 'escapeKeyDown') {
        setIsOpen(false);
      }
    },
    [modal]
  );

  return (
    <DialogContext.Provider value={{ close }}>
      {activateButton(open)}

      <MuiDialog
        open={isOpen}
        onClose={handleDialogClose}
        scroll="paper"
        {...rest}>
        {children}
      </MuiDialog>
    </DialogContext.Provider>
  );
};

export default Dialog;
